/**
 * カスタムJavaScript
 * サイト全体のカスタムスクリプトをここに記述
 * 
 * 命名規則：
 * - カスタム関数は c_ プレフィックスを使用
 * - 例：c_initSlider(), c_handleClick()
 * 
 * このファイルは theme.liquid で読み込まれます
 * theme.js の後に読み込まれるため、既存機能を拡張できます
 * 
 * ========================================
 * 目次
 * ========================================
 * 1. 設定
 * 2. ユーティリティ関数
 * 3. ヘッダー関連の処理
 * 4. フッター関連の処理
 * 5. その他のカスタム処理
 * 6. イベントハンドラー
 * 7. 初期化
 */

(function() {
  'use strict';

  // ========================================
  // 1. 設定
  // ========================================
  
  const c_config = {
    debug: false, // デバッグモード（true にすると console.log が表示されます）
    // 必要な設定をここに追加
  };

  // ========================================
  // 2. ユーティリティ関数
  // ========================================
  
  /**
   * デバウンス処理
   * @param {Function} func - 実行する関数
   * @param {number} wait - 待機時間（ミリ秒）
   * @returns {Function}
   */
  function c_debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * スロットル処理
   * @param {Function} func - 実行する関数
   * @param {number} limit - 制限時間（ミリ秒）
   * @returns {Function}
   */
  function c_throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * デバッグログ
   * @param {string} message - ログメッセージ
   * @param {*} data - ログデータ
   */
  function c_log(message, data) {
    if (c_config.debug) {
      console.log(`[Custom] ${message}`, data || '');
    }
  }

  // ========================================
  // 3. ヘッダー関連の処理
  // ========================================
  
  /**
   * ヘッダーのカスタム処理
   * c_header_corporate.liquid, c_header_ec.liquid で使用
   */
  function c_initHeader() {
    c_log('Header initialized');
    // ヘッダーのカスタム処理をここに追加
  }

  // ========================================
  // 4. フッター関連の処理
  // ========================================
  
  /**
   * フッターのカスタム処理
   * c_footer.liquid で使用
   */
  function c_initFooter() {
    c_log('Footer initialized');
    // フッターのカスタム処理をここに追加
  }

  // ========================================
  // 5. その他のカスタム処理
  // ========================================
  
  /**
   * その他のカスタム初期化処理
   */
  function c_initOthers() {
    // ページ内ナビの初期化
    c_initPageNav();
    // 外部リンクにtarget="_blank"を追加
    c_initExternalLinks();
    // HISTORYタイムライン横スクロール
    c_initHistoryTimeline();
    // 商品バッジ優先順位制御
    c_initProductBadges();
    // 商品詳細ページの売り切れバッジ
    c_initProductSoldOutBadge();
    // スクロールアニメーション（data-anim属性）
    c_initScrollAnim();
    // 販売期間外のスティッキーATCバー非表示
    c_initStickyBarSalesPeriod();
    // カート販売期間外商品の自動除外
    c_initCartSalesPeriodCleanup();
  }

  // ========================================
  // 5-0. 商品詳細ページの売り切れバッジ
  // ========================================
  
  /**
   * 商品詳細ページで売り切れの場合、タイトル上に「SOLD OUT」バッジを表示
   */
  function c_initProductSoldOutBadge() {
    // 商品詳細ページでない場合は終了
    const productInfo = document.querySelector('.product-info');
    if (!productInfo) {
      return;
    }
    
    // 売り切れ判定: カートボタンが無効、または「売り切れ」テキストがある
    const buyButton = document.querySelector('.product-info .button[type="submit"]');
    const isSoldOut = buyButton && (buyButton.disabled || buyButton.textContent.includes('売り切れ') || buyButton.textContent.toLowerCase().includes('sold out'));
    
    if (!isSoldOut) {
      // sold-out-badge要素が存在するか確認（Prestigeのネイティブ機能）
      const existingSoldOutBadge = document.querySelector('.product-info sold-out-badge');
      if (!existingSoldOutBadge) {
        return;
      }
    }
    
    // 既存のbadge-listを探す、なければ作成
    let badgeList = productInfo.querySelector('.badge-list');
    const productTitle = productInfo.querySelector('.product-title');
    
    if (!productTitle) {
      return;
    }
    
    // 既にSOLD OUTバッジがあれば終了
    if (productInfo.querySelector('.badge--sold-out')) {
      return;
    }
    
    // バッジを作成
    const soldOutBadge = document.createElement('span');
    soldOutBadge.className = 'badge badge--sold-out c-product-sold-out-badge';
    soldOutBadge.textContent = 'SOLD OUT';
    
    if (badgeList) {
      // 既存のbadge-listの先頭に追加
      badgeList.insertBefore(soldOutBadge, badgeList.firstChild);
    } else {
      // badge-listを新規作成してタイトルの前に挿入
      badgeList = document.createElement('div');
      badgeList.className = 'badge-list c-product-badge-list';
      badgeList.appendChild(soldOutBadge);
      productTitle.parentNode.insertBefore(badgeList, productTitle);
    }
    
    c_log('Product SOLD OUT badge added');
  }

  // ========================================
  // 5-1. スクロールアニメーション（data-anim属性）
  // ========================================
  
  /**
   * data-anim属性を持つ要素が画面に表示されたらis-animクラスを付与
   * Intersection Observer APIを使用
   */
  function c_initScrollAnim() {
    const animElements = document.querySelectorAll('[data-anim]');
    
    if (animElements.length === 0) {
      return;
    }
    
    // Intersection Observerのオプション
    const observerOptions = {
      root: null, // ビューポートを基準
      rootMargin: '0px 0px -10% 0px', // 下から10%入ったところで発火
      threshold: 0.1 // 10%見えたら発火
    };
    
    // コールバック関数
    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // 画面に表示されたらis-animクラスを付与
          entry.target.classList.add('is-anim');
        } else {
          // 画面から消えたらis-animクラスを外す
          entry.target.classList.remove('is-anim');
        }
      });
    };
    
    // Observerを作成
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    // 各要素を監視
    animElements.forEach(element => {
      observer.observe(element);
    });
    
    c_log('Scroll animation initialized', { count: animElements.length });
  }

  // ========================================
  // 5-2. HISTORYタイムライン横スクロール
  // ========================================
  
  /**
   * HISTORYページのタイムラインセクション
   * 縦スクロールに連動して横スクロールを実現
   */
  function c_initHistoryTimeline() {
    // コーディング中は一時的にJSを無効化
    //return false;

    const section = document.getElementById('history-timeline-section');
    const timeline = document.getElementById('history-timeline');
    
    if (!section || !timeline) {
      c_log('History timeline elements not found');
      return;
    }

    const wrapper = timeline.parentElement;
    
    // 最後のシーンの固定表示時間（縦スクロール量に換算、px）
    const END_SCENE_HOLD_DISTANCE = 1000; // 調整可能
    
    // ヘッダーの高さを取得
    function getHeaderHeight() {
      const header = document.querySelector('.shopify-section-header');
      return header ? header.offsetHeight : 70;
    }

    // タイムラインの全幅を計算
    function getTimelineWidth() {
      let totalWidth = 0;
      const children = timeline.children;
      for (let i = 0; i < children.length; i++) {
        totalWidth += children[i].offsetWidth;
      }
      return totalWidth;
    }
    
    // 横スクロールする距離を計算（最後のシーンが表示されるまで）
    function getHorizontalScrollDistance() {
      const timelineWidth = getTimelineWidth();
      const viewportWidth = window.innerWidth;
      // 横スクロール距離 = 全幅 - ビューポート幅
      return timelineWidth - viewportWidth;
    }

    // セクションの高さを設定（横スクロール分 + 最後のシーン固定表示分）
    function setSectionHeight() {
      const scrollDistance = getHorizontalScrollDistance();
      const viewportHeight = window.innerHeight - getHeaderHeight();
      
      // セクションの高さ = ビューポート高さ + 横スクロール距離 + 最後のシーン固定表示分
      const sectionHeight = viewportHeight + scrollDistance + END_SCENE_HOLD_DISTANCE;
      section.style.height = sectionHeight + 'px';
      
      c_log('Horizontal scroll distance:', scrollDistance);
      c_log('End scene hold distance:', END_SCENE_HOLD_DISTANCE);
      c_log('Section height:', sectionHeight);
      
      return {
        scrollDistance: scrollDistance,
        sectionHeight: sectionHeight
      };
    }

    // スクロール処理
    function handleScroll() {
      const headerHeight = getHeaderHeight();
      const sectionRect = section.getBoundingClientRect();
      const scrollDistance = getHorizontalScrollDistance();
      const totalScrollRange = scrollDistance + END_SCENE_HOLD_DISTANCE;
      
      // セクションがヘッダーの下に来ているかチェック
      const sectionTop = sectionRect.top;
      const sectionBottom = sectionRect.bottom;
      const viewportHeight = window.innerHeight - headerHeight;
      
      if (sectionTop <= headerHeight && sectionBottom >= viewportHeight + headerHeight) {
        // セクションが固定されている状態
        const scrolledInSection = headerHeight - sectionTop;
        
        if (scrolledInSection <= scrollDistance) {
          // 横スクロール中
          const progress = Math.max(0, Math.min(1, scrolledInSection / scrollDistance));
          const translateX = -progress * scrollDistance;
          timeline.style.transform = `translateX(${translateX}px)`;
          
          // 背景色のシーン切り替え
          updateTimelineScene(progress, scrollDistance);
        } else {
          // 最後のシーン固定表示中（横スクロール完了後）
          timeline.style.transform = `translateX(${-scrollDistance}px)`;
          timeline.className = 'c-history__timeline is-end';
        }
        
      } else if (sectionTop > headerHeight) {
        // セクションがまだ上にある（スクロール前）
        timeline.style.transform = 'translateX(0)';
        timeline.className = 'c-history__timeline is-scene1';
      } else {
        // セクションを通過した（スクロール後）
        timeline.style.transform = `translateX(${-scrollDistance}px)`;
        timeline.className = 'c-history__timeline is-end';
      }
    }

    // シーンの更新（背景色切り替え）
    function updateTimelineScene(progress, scrollDistance) {
      const scene1Width = timeline.children[0]?.offsetWidth || 0;
      const scene2Width = timeline.children[1]?.offsetWidth || 0;
      const scene3Width = timeline.children[2]?.offsetWidth || 0;
      
      const currentPos = progress * scrollDistance;
      
      let sceneClass = 'is-scene1';
      // is-end: 横スクロールが完全に終わった時（progress = 1）
      if (progress >= 1) {
        sceneClass = 'is-end';
      } else if (currentPos >= scene1Width + scene2Width - window.innerWidth * 0.5) {
        sceneClass = 'is-scene3';
      } else if (currentPos >= scene1Width - window.innerWidth * 0.5) {
        sceneClass = 'is-scene2';
      }
      
      timeline.className = 'c-history__timeline ' + sceneClass;
    }

    // 初期化
    function init() {
      setSectionHeight();
      handleScroll();
    }

    // リサイズ時に再計算
    const handleResize = c_debounce(function() {
      setSectionHeight();
      handleScroll();
    }, 150);

    // イベントリスナー登録
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    
    // 初期化実行
    // 少し遅延させて確実にレンダリング後に実行
    requestAnimationFrame(function() {
      requestAnimationFrame(init);
    });

    c_log('History timeline initialized');
  }

  // ========================================
  // 5-1. 商品バッジ優先順位制御
  // ========================================
  
  /**
   * 商品バッジの優先順位制御
   * 優先順位: SOLDOUT > おすすめ > NEW
   * CSS :has() のフォールバック + 動的コンテンツ対応
   */
  function c_initProductBadges() {
    // バッジの優先順位を処理
    function processBadges() {
      const badgeLists = document.querySelectorAll('.badge-list');
      
      badgeLists.forEach(function(badgeList) {
        const badges = badgeList.querySelectorAll('.badge');
        if (badges.length <= 1) return;
        
        // バッジの種類を検出
        let hasSoldOut = false;
        let hasRecommend = false;
        let hasNew = false;
        
        badges.forEach(function(badge) {
          if (badge.classList.contains('badge--sold-out')) hasSoldOut = true;
          if (badge.classList.contains('badge--recommend')) hasRecommend = true;
          if (badge.classList.contains('badge--new')) hasNew = true;
        });
        
        // 優先順位に基づいて非表示制御
        badges.forEach(function(badge) {
          // SOLDOUTがある場合、他のバッジを非表示
          if (hasSoldOut && !badge.classList.contains('badge--sold-out')) {
            badge.style.display = 'none';
          }
          // recommendがある場合（SOLDOUTなし）、NEWを非表示
          else if (!hasSoldOut && hasRecommend && badge.classList.contains('badge--new')) {
            badge.style.display = 'none';
          }
        });
      });
    }
    
    // 初回実行
    processBadges();
    
    // 動的コンテンツ対応（MutationObserver）
    const observer = new MutationObserver(c_debounce(processBadges, 100));
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    c_log('Product badges initialized');
  }

  // ========================================
  // 5-2. 外部リンク処理
  // ========================================
  
  /**
   * 外部リンク（別ドメイン）に target="_blank" を自動追加
   * セキュリティのため rel="noopener noreferrer" も追加
   */
  function c_initExternalLinks() {
    const currentHost = window.location.hostname;
    const links = document.querySelectorAll('a[href]');
    
    links.forEach(function(link) {
      const href = link.getAttribute('href');
      
      // 空、#始まり、javascript:、mailto:、tel: はスキップ
      if (!href || 
          href.startsWith('#') || 
          href.startsWith('javascript:') || 
          href.startsWith('mailto:') || 
          href.startsWith('tel:')) {
        return;
      }

      // 絶対URLかどうかチェック
      try {
        const url = new URL(href, window.location.origin);
        
        // 異なるドメインの場合
        if (url.hostname !== currentHost) {
          // 既にtarget属性がある場合はスキップ
          if (!link.hasAttribute('target')) {
            link.setAttribute('target', '_blank');
          }
          // セキュリティ対策：rel属性を追加
          const existingRel = link.getAttribute('rel') || '';
          if (!existingRel.includes('noopener')) {
            link.setAttribute('rel', (existingRel + ' noopener noreferrer').trim());
          }
        }
      } catch (e) {
        // 無効なURLの場合はスキップ
      }
    });

    c_log('External links initialized');
  }

  // ========================================
  // 5-3. ページ内ナビゲーション
  // ========================================
  
  /**
   * ページ内ナビゲーションの初期化
   * c_page_nav.liquid で使用
   * 各セクションの data-page-nav-name 属性からナビ項目を自動生成
   */
  function c_initPageNav() {
    const navContainers = document.querySelectorAll('.c-page-nav__list');
    
    console.log('[c_initPageNav] navContainers:', navContainers.length);
    
    if (navContainers.length === 0) return;

    // ページ内の全セクションからナビ情報を収集
    const navSections = document.querySelectorAll('[data-page-nav-name]');
    
    console.log('[c_initPageNav] navSections:', navSections.length, navSections);
    
    navContainers.forEach(function(navContainer) {
      // 既にナビ項目がある場合はスキップ
      if (navContainer.children.length > 0) return;

      if (navSections.length === 0) {
        // ナビ項目がない場合はナビ自体を非表示
        const navWrapper = navContainer.closest('.c-page-nav');
        if (navWrapper) navWrapper.style.display = 'none';
        return;
      }

      // ナビ項目を生成
      navSections.forEach(function(section) {
        const navName = section.getAttribute('data-page-nav-name');
        const sectionId = section.id;
        
        if (navName && sectionId) {
          const navItem = document.createElement('a');
          navItem.href = '#' + sectionId;
          navItem.className = 'c-page-nav__item';
          navItem.textContent = navName;
          
          // スムーススクロール
          navItem.addEventListener('click', function(e) {
            e.preventDefault();
            c_scrollToSection(sectionId);
          });
          
          navContainer.appendChild(navItem);
        }
      });
    });

    c_log('Page nav initialized', navSections.length + ' sections found');
  }

  /**
   * 指定セクションへスムーススクロール
   * @param {string} sectionId - スクロール先のセクションID
   */
  function c_scrollToSection(sectionId) {
    const target = document.getElementById(sectionId);
    if (!target) return;

    const headerHeight = document.querySelector('.shopify-section-header')?.offsetHeight || 0;
    const offset = headerHeight + 20;
    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - offset;
    
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  }

  // ========================================
  // 5-X. カート販売期間外商品の自動除外
  // ========================================

  /**
   * カートページ表示時に販売期間外の商品を自動除外する
   * main-cart.liquid で埋め込んだ data-sales-status 属性を参照
   */
  /**
   * 販売期間外の商品詳細ページでスティッキーATCバーを非表示にする
   * main-product.liquid のスティッキーバーは buy-buttons.liquid を経由しないため、
   * JS で data-sales-status 属性を参照して制御する
   */
  function c_initStickyBarSalesPeriod() {
    var notice = document.querySelector('.c-sales-period-notice[data-sales-status="coming_soon"], .c-sales-period-notice[data-sales-status="ended"]');
    if (!notice) return;

    var stickyBar = document.querySelector('product-sticky-bar');
    if (stickyBar) {
      stickyBar.style.display = 'none';
    }
  }

  function c_initCartSalesPeriodCleanup() {
    var cartItemsContainer = document.getElementById('c-cart-items');
    if (!cartItemsContainer) return;

    var rows = cartItemsContainer.querySelectorAll('tr[data-sales-status]');
    var itemsToRemove = [];

    rows.forEach(function(row) {
      var status = row.getAttribute('data-sales-status');
      if (status === 'coming_soon' || status === 'ended') {
        itemsToRemove.push({
          key: row.getAttribute('data-line-item-key'),
          title: row.getAttribute('data-product-title') || '不明な商品',
          status: status
        });
      }
    });

    if (itemsToRemove.length === 0) return;

    c_log('販売期間外商品を検出', itemsToRemove);

    var updates = {};
    itemsToRemove.forEach(function(item) {
      updates[item.key] = 0;
    });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
    .then(function(response) {
      if (!response.ok) throw new Error('カート更新に失敗しました');
      return response.json();
    })
    .then(function() {
      var removedNames = itemsToRemove.map(function(item) {
        var reason = item.status === 'coming_soon' ? '販売開始前' : '販売終了';
        return '・' + item.title + '（' + reason + '）';
      }).join('\n');

      alert(
        '販売期間外の商品がカートに含まれていたため、除外しました。\n\n' +
        removedNames
      );

      window.location.reload();
    })
    .catch(function(err) {
      c_log('カート自動除外エラー', err);
    });
  }

  // ========================================
  // 6. イベントハンドラー
  // ========================================
  
  /**
   * カスタムイベント処理の例
   */
  function c_handleCustomEvent(event) {
    c_log('Custom event triggered', event);
    // カスタムイベント処理をここに追加
  }

  /**
   * スクロールイベント処理の例
   */
  const c_handleScroll = c_throttle(function() {
    const scrollY = window.scrollY;
    c_log('Scroll position', scrollY);
    // スクロール処理をここに追加
  }, 100);

  /**
   * リサイズイベント処理の例
   */
  const c_handleResize = c_debounce(function() {
    const width = window.innerWidth;
    c_log('Window width', width);
    // リサイズ処理をここに追加
  }, 250);

  // ========================================
  // 7. 初期化関数
  // ========================================
  
  /**
   * カスタムスクリプトの初期化
   */
  function c_init() {
    c_log('Custom JS initialized');

    // 各コンポーネントの初期化
    c_initHeader();
    c_initFooter();
    c_initOthers();

    // イベントリスナーの登録
    // window.addEventListener('scroll', c_handleScroll);
    // window.addEventListener('resize', c_handleResize);

    // カスタム要素の初期化
    // 必要な初期化処理をここに追加
  }

  // ========================================
  // 実行
  // ========================================
  
  // DOMContentLoaded後に初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', c_init);
  } else {
    // DOMが既に読み込まれている場合はすぐに実行
    c_init();
  }

  // ========================================
  // グローバルに公開（必要な場合）
  // ========================================
  
  // 他のスクリプトから使用したい関数がある場合
  // window.customUtils = {
  //   debounce: c_debounce,
  //   throttle: c_throttle,
  //   log: c_log
  // };

})();

